import { useRef, useState } from "react";
import { useProperty } from "@/contexts/PropertyContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Image as ImageIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Settings card — upload a custom cover photo for each community the
 * admin manages. Feeds the public signup preview via
 * `POST /api/admin/properties/{pid}/cover` → sets `properties.hero_image` to a
 * cache-busted `/api/onboarding/community-cover/{pid}` URL.
 */
export function CommunityCoverCard() {
  const { properties, refresh } = useProperty();
  const [uploadingId, setUploadingId] = useState(null);
  const [bump, setBump] = useState(0); // force <img> re-fetch after upload
  const inputRefs = useRef({});

  const upload = async (pid, file) => {
    if (!file) return;
    setUploadingId(pid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/properties/${pid}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover updated");
      setBump((b) => b + 1);
      refresh?.();
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const pickFile = (pid) => inputRefs.current[pid]?.click();

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="community-cover-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Branding</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-orange-400" /> Community cover photos
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            The cover appears on the resident signup preview + property switcher. 10 MB max, JPG/PNG/WebP.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {properties.map((p) => (
          <div key={p.id} className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`community-cover-row-${p.id}`}>
            <div className="relative h-32 w-full bg-zinc-900">
              {p.hero_image ? (
                <img
                  src={p.hero_image + (p.hero_image.includes("?") ? `&_=${bump}` : `?_=${bump}`)}
                  alt={p.name}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  data-testid={`community-cover-img-${p.id}`}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-zinc-600">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-2 left-3 right-3">
                <div className="text-white text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-zinc-300 truncate">{p.type} · {p.units} units</div>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="text-[11px] text-zinc-500 truncate">
                {p.hero_image_updated_at ? `Updated ${(p.hero_image_updated_at || "").slice(0, 10)}` : "Using default cover"}
              </div>
              <input
                ref={(el) => { inputRefs.current[p.id] = el; }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { upload(p.id, e.target.files?.[0]); e.target.value = ""; }}
                data-testid={`community-cover-file-${p.id}`}
              />
              <Button
                size="sm"
                onClick={() => pickFile(p.id)}
                disabled={uploadingId === p.id}
                className="rounded-full bg-orange-500 hover:bg-orange-600"
                data-testid={`community-cover-upload-${p.id}`}
              >
                {uploadingId === p.id ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading</>
                ) : (
                  <><Camera className="h-3.5 w-3.5 mr-1" /> Replace</>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
